<template>
    <v-container class="fill-height" fluid>
        <v-container class="pa-0">
            <v-row align="center" justify="center">
                <v-col cols="12" sm="8" md="6" lg="5" xl="4">
                    <v-form @submit.prevent="saveNewPassword">
                        <v-card>
                            <v-toolbar flat dark color="secondary">
                                <v-toolbar-title>{{ t('common.account.passwordReset.title') }}</v-toolbar-title>
                            </v-toolbar>

                            <v-container>
                                <v-row justify="center">
                                    <v-col cols="12">
                                        <v-text-field
                                            v-model="newPassword"
                                            :label="t('common.account.passwordReset.newPasswordLabel')"
                                            :type="showPassword ? 'text' : 'password'"
                                            :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                            counter
                                            :hint="t('common.account.passwordReset.passwordHint')"
                                            variant="filled"
                                            prepend-inner-icon="mdi-lock"
                                            autocomplete="new-password"
                                            :error-messages="newPasswordErrors"
                                            @input="v$.newPassword.$touch()"
                                            @click:append-inner="showPassword = !showPassword"
                                        />
                                        <v-text-field
                                            v-model="newPasswordConfirm"
                                            :label="t('common.account.passwordReset.repeatPasswordLabel')"
                                            :type="showPasswordRepeat ? 'text' : 'password'"
                                            :append-inner-icon="showPasswordRepeat ? 'mdi-eye' : 'mdi-eye-off'"
                                            counter
                                            persistent-hint
                                            variant="filled"
                                            prepend-inner-icon="mdi-lock"
                                            autocomplete="new-password"
                                            :error-messages="newPasswordConfirmError"
                                            @input="v$.newPasswordConfirm.$touch()"
                                            @click:append-inner="showPasswordRepeat = !showPasswordRepeat"
                                        />
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-btn
                                            type="submit"
                                            color="success"
                                            size="default"
                                            class="mt-1"
                                            data-sentry-label="Zaloguj"
                                            :loading="actionLoading"
                                        >
                                            {{ t('common.account.passwordReset.submitButton') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-card>
                    </v-form>
                </v-col>
            </v-row>
        </v-container>
    </v-container>
</template>

<script setup>
/*
  Organization / ordering applied:
  1. Imports
  2. Props / Emits
  3. Constants
  4. Stores & top-level services
  5. Reactive state (refs/reactive)
  6. Validation rules & setup
  7. Computed
  8. Handlers / methods
  9. Helpers / lifecycle
*/

// 1. Imports
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { errorHandler } from '@/composables/errorHandler'
import { t } from '@/i18n'
import { useCommonStore, useUserStore } from '@/stores'
import { passwordRequirements } from '@/utils/customValidators'

// 2. Props / Emits
// (none)

// 3. Constants
const route = useRoute()
const router = useRouter()

// 4. Stores & top-level services
const commonStore = useCommonStore()
const userStore = useUserStore()

// 5. Reactive state (refs/reactive)
const newPassword = ref(null)
const newPasswordConfirm = ref(null)
const actionLoading = ref(false)
const showPassword = ref(false)
const showPasswordRepeat = ref(false)

// 6. Validation rules & setup
const rules = {
    newPassword: { required, passwordRequirements },
    newPasswordConfirm: { required, sameAsNewPassword: sameAs(newPassword) }
}
const v$ = useVuelidate(rules, { newPassword, newPasswordConfirm })

// 7. Computed
const token = computed(() => route.query.token ?? null)

const newPasswordErrors = computed(() => {
    if (!v$.value.newPassword.$dirty) return ''

    const errors = []
    if (v$.value.newPassword.required.$invalid) errors.push(t('common.account.passwordReset.newPasswordRequired'))
    if (v$.value.newPassword.passwordRequirements.$invalid) {
        errors.push(t('common.account.passwordReset.passwordRequirements'))
    }

    return errors.length ? Object.freeze(errors) : ''
})

const newPasswordConfirmError = computed(() => {
    if (!v$.value.newPasswordConfirm.$dirty) return ''

    const errors = []
    if (v$.value.newPasswordConfirm.required.$invalid) errors.push(t('common.account.passwordReset.repeatPasswordRequired'))
    if (v$.value.newPasswordConfirm.sameAsNewPassword.$invalid) errors.push(t('common.account.passwordReset.passwordsMustMatch'))

    return errors.length ? Object.freeze(errors) : ''
})

// 8. Handlers / methods
async function saveNewPassword() {
    const isValid = await v$.value.$validate()

    if (!isValid) {
        commonStore.showNotification({
            type: 'error',
            message: t('common.validations.invalidMessage')
        })
        return
    }

    if (!token.value) {
        commonStore.showNotification({
            type: 'error',
            message: t('common.account.passwordReset.tokenMissing')
        })
        return
    }

    actionLoading.value = true

    try {
        await userStore.passwordReset({
            newPassword: newPassword.value,
            newPasswordConfirm: newPasswordConfirm.value,
            token: token.value
        })

        await router.push({ name: 'userLogin' })
    } catch (error) {
        errorHandler(error)
    } finally {
        actionLoading.value = false
        newPassword.value = null
        newPasswordConfirm.value = null
        showPassword.value = false
        showPasswordRepeat.value = false
        v$.value.$reset()
    }
}

// 9. Helpers / lifecycle
// (none)
</script>
